/**
 * Client calls for the secure vault magic-link entry flow.
 *
 * Every call passes `throwOnError: true` and lets the axios error through, so
 * the page can show it with `getUserFacingApiErrorMessage`. Without the flag
 * the SDK returns a 4xx as an ordinary result: an expired link crashed into a
 * generic error and a refused save showed "All set" (GET-18350).
 * secretRequestApis.sim.test.ts pins both.
 *
 * NOTE: the imported SDK symbols are generated from the backend OpenAPI spec by
 * `npm run generate-types` (run against a running backend after the
 * /api/v1/vault router was added). Until that runs, these imports won't resolve.
 */

import {
  getVaultSecretRequestApiV1VaultSecretRequestTokenGet,
  submitVaultSecretsApiV1VaultSecretsPost,
  type GetVaultSecretRequestApiV1VaultSecretRequestTokenGetResponses,
  type SubmitVaultSecretsApiV1VaultSecretsPostResponses,
  type VaultFieldErrorResponse,
} from "../client";
import {
  getApiErrorStatus,
} from "./api";

export type VaultSecretRequestDetails =
  GetVaultSecretRequestApiV1VaultSecretRequestTokenGetResponses[200];

export type VaultSecretSubmitResponse =
  SubmitVaultSecretsApiV1VaultSecretsPostResponses[200];

export async function getVaultSecretRequestDetails(
  token: string,
): Promise<VaultSecretRequestDetails> {
  const response = await getVaultSecretRequestApiV1VaultSecretRequestTokenGet({
    path: { token },
    throwOnError: true,
  });
  return response.data;
}

export async function submitVaultSecrets(
  token: string,
  values: Record<string, string>,
  companionValues: Record<string, Record<string, string>> = {},
): Promise<VaultSecretSubmitResponse> {
  const response = await submitVaultSecretsApiV1VaultSecretsPost({
    body: { token, values, companion_values: companionValues },
    throwOnError: true,
  });
  return response.data;
}

/**
 * The values a refused save names, so the form can mark each one (GET-15087).
 * Empty when the problem is the link itself (expired, already used) rather
 * than something the owner typed. Nothing was saved and the link still works.
 */
export function getVaultFieldErrors(err: unknown): VaultFieldErrorResponse[] {
  if (getApiErrorStatus(err) !== 400) return [];
  const fieldErrors = (
    err as { response?: { data?: { field_errors?: unknown } } }
  )?.response?.data?.field_errors;
  if (!Array.isArray(fieldErrors)) return [];
  return fieldErrors.filter(
    (e): e is VaultFieldErrorResponse =>
      typeof e?.subject === "string" &&
      typeof e?.key === "string" &&
      typeof e?.message === "string",
  );
}
